'use client'

import { ArrowUp, Instagram, type LucideIcon, Send } from 'lucide-react'
import Link from 'next/link'

import {
  COMPANY_ADDRESS,
  COMPANY_EMAIL,
  COMPANY_PHONE,
  COMPANY_PHONE_RAW,
  COMPANY_SOCIALS,
} from '@/lib/seo'

export default function Footer() {
  const socialItems = [
    { name: 'Instagram', icon: Instagram, href: COMPANY_SOCIALS.instagram },
    { name: 'Telegram', icon: Send, href: COMPANY_SOCIALS.telegram },
  ].filter((item): item is { name: string; icon: LucideIcon; href: string } => Boolean(item.href))

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const currentYear = new Date().getFullYear()

  return (
    <footer className="relative z-20 overflow-hidden border-t border-white/10 bg-transparent pt-20 pb-10 text-white sm:pt-24 sm:pb-12">
      <div className="home-container relative z-10">
        <div className="mb-16 grid grid-cols-1 gap-10 sm:mb-20 md:grid-cols-12 md:gap-x-8 md:gap-y-12 xl:gap-12">
          <div className="md:col-span-12 xl:col-span-4">
            <Link href="/" className="home-card-title-sm mb-6 block tracking-tight">
              Цифровой горизонт
            </Link>
            <p className="home-subnote max-w-md leading-relaxed text-gray-400">
              Digital-агентство полного цикла в Майкопе. Создание сайтов, SMM, ИИ-агенты и
              интернет-маркетинг для бизнеса в Республике Адыгея.
            </p>
          </div>

          <div className="md:col-span-4 xl:col-span-2">
            <h3 className="home-meta mb-6 text-gray-400">Меню</h3>
            <ul className="space-y-4">
              {[
                { name: 'Услуги', href: '/services' },
                { name: 'Направления', href: '/directions' },
                { name: 'Кейсы', href: '/cases' },
                { name: 'Агентство', href: '/agency' },
                { name: 'Новости', href: '/news' },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    href={item.href}
                    className="home-subnote text-gray-300 transition-colors hover:text-white"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4 xl:col-span-3">
            <h3 className="home-meta mb-6 text-gray-400">Социальные сети</h3>
            {socialItems.length > 0 ? (
              <ul className="space-y-4">
                {socialItems.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="home-subnote group inline-flex items-center gap-3 text-gray-300 transition-colors hover:text-white"
                    >
                      <item.icon className="h-5 w-5 opacity-60 transition-opacity group-hover:opacity-100" />
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="home-subnote text-gray-400">Ссылки на соцсети обновляются.</p>
            )}
          </div>

          <div className="md:col-span-4 xl:col-span-3">
            <h3 className="home-meta mb-6 text-gray-400">Контакты</h3>
            <a
              href={`mailto:${COMPANY_EMAIL}`}
              className="home-card-title-sm mb-2 block break-all transition-colors hover:text-blue-300"
            >
              {COMPANY_EMAIL}
            </a>
            <a
              href={`tel:${COMPANY_PHONE_RAW}`}
              className="home-card-title-sm mb-6 block transition-colors hover:text-blue-300"
            >
              {COMPANY_PHONE}
            </a>
            <address className="home-subnote text-gray-400 not-italic">
              г. {COMPANY_ADDRESS.city}, {COMPANY_ADDRESS.street}
              <br />
              {COMPANY_ADDRESS.region}
            </address>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-5 border-t border-white/5 pt-6 sm:gap-6 sm:pt-8 lg:flex-row">
          <div className="home-caption text-center text-gray-400 lg:text-left">
            © 2024–{currentYear} Цифровой горизонт. Все права защищены.
          </div>

          <div className="home-caption flex flex-wrap justify-center gap-5 text-gray-400 sm:gap-6">
            <Link href="/privacy" className="transition-colors hover:text-white">
              Политика конфиденциальности
            </Link>
            <Link href="/terms" className="transition-colors hover:text-white">
              Пользовательское соглашение
            </Link>
            <Link href="/cookies" className="transition-colors hover:text-white">
              Cookies
            </Link>
            <Link href="/maykop" className="transition-colors hover:text-white">
              Услуги в Майкопе
            </Link>
          </div>

          <button
            onClick={scrollToTop}
            className="group flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 transition-all hover:border-white/20 hover:bg-white/10 sm:h-12 sm:w-12"
            aria-label="Наверх"
          >
            <ArrowUp className="h-5 w-5 text-gray-400 transition-colors group-hover:text-white" />
          </button>
        </div>
      </div>
    </footer>
  )
}
